/**
 * The welcome flow (Stage 10, Sam 2026-10-26): after a brand-new account's first Join or group join, five steps on the
 * real pages - Sets, the Map, Find, your profile, people to follow. The first three are a show, not the real thing: they
 * open on Portola 2026 (past, but it has set times and a good map) with you in the middle of the grounds and four made-up
 * friends (lib/map/mapScenarios 'welcome'), and picks stay on the phone. The last two are real: your photo and name, and
 * follows.
 *
 * Demo mode is the URL (?demo=welcome), so every step is an ordinary page with a link; the bar (WelcomeBar) moves
 * between them, the dock is hidden, and nothing in the demo writes to the server.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '../supabaseClient';

export const WELCOME_EVENT_ID = 'evt_portola_2026';
const E = encodeURIComponent(WELCOME_EVENT_ID);

const STEP = {
  sets: { key: 'sets', line: 'Pick your sets', path: `/sets/${E}?demo=welcome` },
  map: { key: 'map', line: 'Your festival map', path: `/map/${E}?demo=welcome` },
  find: { key: 'find', line: 'Find your friends', path: `/find?demo=welcome&event=${E}` },
  profile: { key: 'profile', line: 'Add your profile', path: '/welcome/profile?demo=welcome' },
  people: { key: 'people', line: 'Follow your friends', path: '/people?demo=welcome' },
};

/**
 * Who gets which steps (Sam, 2026-10-27; it replaced the three-card intro too):
 *   - the app: Sets, Map, Find, then your profile and follows;
 *   - a browser: no Find step - it cannot run there, and the web's get-the-app prompt already says so;
 *   - invited (came in on a group link): the real steps first - your photo and name are how the group knows you on
 *     Find - then the show.
 */
export function welcomeSteps(invited = wasInvited()) {
  const find = Capacitor.isNativePlatform() ? [STEP.find] : [];
  return invited
    ? [STEP.profile, STEP.people, STEP.sets, STEP.map, ...find]
    : [STEP.sets, STEP.map, ...find, STEP.profile, STEP.people];
}

export function isWelcomeDemo(search) {
  return typeof search === 'string' && new URLSearchParams(search).get('demo') === 'welcome';
}

/** Which step a demo URL is. */
export function welcomeStepFor(location) {
  if (!isWelcomeDemo(location?.search)) return null;
  const p = location.pathname;
  if (p.startsWith('/sets/')) return 'sets';
  if (p.startsWith('/map/')) return 'map';
  if (p === '/find') return 'find';
  if (p === '/welcome/profile') return 'profile';
  if (p === '/people' || p === '/profile/discover') return 'people';
  return null;
}

const DONE_KEY = (userId) => `prii.welcome.done.${userId}`;
const RETURN_KEY = 'prii.welcome.return';
const INVITED_KEY = 'prii.welcome.invited';
const INVITED_FOR_MS = 24 * 60 * 60 * 1000;

/** Done on the account (user_metadata.welcome_done), so a phone and a browser never both run it; the phone's own key
 * answers at once and offline. */
export function welcomeDone(user) {
  if (!user?.id || user.user_metadata?.welcome_done) return true;
  try {
    return window.localStorage.getItem(DONE_KEY(user.id)) === '1';
  } catch {
    return true;
  }
}

function markWelcomeDone(userId) {
  try {
    window.localStorage.setItem(DONE_KEY(userId), '1');
  } catch {
    /* private mode */
  }
  void supabase.auth.updateUser({ data: { welcome_done: true } }).catch(() => {});
}

/** A group link opened on this phone in the last day: its owner came in invited. */
export function noteInvite() {
  try {
    window.localStorage.setItem(INVITED_KEY, String(Date.now()));
  } catch {
    /* private mode */
  }
}

export function wasInvited() {
  try {
    return Date.now() - Number(window.localStorage.getItem(INVITED_KEY) || 0) < INVITED_FOR_MS;
  } catch {
    return false;
  }
}

export const welcomeAvailable = () => Capacitor.isNativePlatform();

/** Brand new: the account is a few days old and has not been through the flow. */
export function shouldStartWelcome(user) {
  // The app only (Sam, 2026-10-28): a browser never starts it, and Settings hides it there.
  if (!Capacitor.isNativePlatform() || welcomeDone(user)) return false;
  const created = Date.parse(user.created_at ?? '');
  return Number.isFinite(created) && Date.now() - created < 3 * 24 * 60 * 60 * 1000;
}

export function startWelcome(navigate, returnTo) {
  try {
    window.sessionStorage.setItem(RETURN_KEY, returnTo || '/');
  } catch {
    /* private mode */
  }
  navigate(welcomeSteps()[0].path);
}

/**
 * Leaving the app mid-flow (home, lock, another app) counts as Skip (Sam, 2026-11-04): coming back put you on the same
 * step, and a relaunch started it over from step one. Done is saved as you leave, since iOS may never bring this page
 * back; coming back to it finishes the flow onto the page you started from.
 */
export function leaveWelcome(userId) {
  if (userId) markWelcomeDone(userId);
}

/** Not now, on the invited offer: done, and you stay where you are. Settings can still run it. */
export function declineWelcome(userId) {
  markWelcomeDone(userId);
}

export function finishWelcome(navigate, userId) {
  markWelcomeDone(userId);
  let back = '/';
  try {
    back = window.sessionStorage.getItem(RETURN_KEY) || '/';
    window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* private mode */
  }
  navigate(back, { replace: true });
}

/** A step can ask Next to wait for it (the profile step saves first). One at a time; the step clears it on unmount. */
let beforeNext = null;
export function setWelcomeBeforeNext(fn) {
  beforeNext = fn;
}
export async function runWelcomeBeforeNext() {
  return beforeNext ? beforeNext() : true;
}

/** The show's four friends - the same Maya, Leo, Ava and Jordan the map step puts around you (mapScenarios 'welcome'). */
export const WELCOME_FRIENDS = [
  { id: 'welcome-maya', display_name: 'Maya' },
  { id: 'welcome-leo', display_name: 'Leo' },
  { id: 'welcome-ava', display_name: 'Ava' },
  { id: 'welcome-jordan', display_name: 'Jordan' },
];

/**
 * Their picks on the Sets step (Sam, 2026-10-31: show the group side of picking): the same every time, a few acts
 * per day. Each day's top of the bill draws most of them - three or four on the first act, then fewer - and a handful
 * further down get one or two, so the list reads like a real group's. { [artist_id]: voters }, as the group's would be.
 */
export function welcomeFriendPicks(lineup) {
  const byDay = new Map();
  for (const row of lineup) {
    if (!row.performance_day) continue;
    if (!byDay.has(row.performance_day)) byDay.set(row.performance_day, []);
    byDay.get(row.performance_day).push(row);
  }
  const hash = (s) => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
    return (h >>> 0) / 4294967296;
  };
  const out = {};
  const TOP = [4, 3, 2, 2]; // friends on the day's top four acts by billing
  for (const rows of byDay.values()) {
    const billed = [...rows].sort((a, b) => (a.billing_order ?? 999) - (b.billing_order ?? 999));
    billed.forEach((row, i) => {
      const n =
        i < TOP.length
          ? TOP[i]
          : hash(`${row.artist_id}|n`) < 0.18
            ? hash(`${row.artist_id}|m`) < 0.4
              ? 2
              : 1
            : 0;
      if (!n) return;
      const start = Math.floor(hash(String(row.artist_id)) * WELCOME_FRIENDS.length);
      out[row.artist_id] = Array.from(
        { length: n },
        (_, k) => WELCOME_FRIENDS[(start + k) % WELCOME_FRIENDS.length],
      );
    });
  }
  return out;
}
